import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useEffect, useRef, useState } from "react";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { Button } from "@progress/kendo-react-buttons";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  createCity,
  getAllActiveCities,
  getAllCities,
  getCityById,
  updateCity,
} from "./services/city.services";
import { clearCityDetails } from "./citySlice";
import { closed } from "../../components/drawer/drawerSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import { getAllActiveCountries } from "../country/services/country.services";
import { findAllActiveStates } from "../state/services/state.services";
import { IState } from "../state/stateModel";
import { findIndex } from "lodash";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";

interface StateChangeWatcherProps {
  formRenderProps: FormRenderProps;
  StateList: IState[];
}
const StateChangeWatcher: React.FC<StateChangeWatcherProps> = ({
  StateList,
  formRenderProps,
}) => {
  const state_id = formRenderProps.valueGetter("state_id");

  useEffect(() => {
    const StateID = state_id ? state_id : null;
    const stateIndex = findIndex(StateList, { id: StateID });
    if (stateIndex > -1) {
      const state = StateList[stateIndex];
      formRenderProps.onChange("country_id", { value: state.country_id });
    } else {
      formRenderProps.onChange("state_id", { value: "" });
      formRenderProps.onChange("country_id", { value: "" });
    }
  }, [state_id]);
  return null;
};

const CreateCity: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CountryList = useAppSelector((state) => state.country.CountryList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const CityDetail = useAppSelector((state) => state.city.CityDetail);
  const city_guid = useAppSelector((state) => state.drawer.data);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);
  const formLoading = useAppSelector((state) => state.city.formLoading);
  const [formKey, setFormKey] = useState(1);
  useEffect(() => {
    setFormKey(formKey + 1);
  }, [StateList]);

  useEffect(() => {
    dispatch(getAllActiveCountries());
    dispatch(findAllActiveStates());
    return () => {
      dispatch(clearCityDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (city_guid) {
      dispatch(getCityById(city_guid));
    }
  }, [city_guid]);

  const handleSubmit = async (values: any) => {
    try {
      if (!city_guid) {
        const payload = {
          city_name: values.city_name,
          state_id: values.state_id,
          country_id: values.country_id,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(createCity(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearCityDetails());
          if (isIndirect) {
            dispatch(getAllActiveCities());
          } else {
            dispatch(getAllCities());
          }
        }
      } else {
        const payload = {
          city_guid: city_guid,
          city_name: values.city_name,
          state_id: values.state_id,
          country_id: values.country_id,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(updateCity(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearCityDetails());
          dispatch(getAllCities());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <Form
      onSubmit={handleSubmit}
      key={formKey}
      initialValues={CityDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem className="master-drawer-header">
              <Typography.h4 className="m-0">
                {city_guid ? "Update City" : "Create City"}
              </Typography.h4>
              <Button
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
              >
                <RxCross1 />
              </Button>
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="city_name"
                label="City Name"
                component={FormTextField}
                placeholder="City Name"
                astrike={true}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name={"state_id"}
                label={"State Name"}
                placeholder="State Name"
                component={FormSelectionField}
                validator={requiredValidator}
                astrike={true}
                options={StateList?.map((state: any) => {
                  return {
                    value: state.id,
                    label: state.state_name,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name={"country_id"}
                label={"Country Name"}
                component={FormSelectionField}
                placeholder="Country Name"
                disabled={true}
                // validator={requiredValidator}
                options={CountryList.map((country: any) => {
                  return {
                    value: country.id,
                    label: country.country_name,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "10px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label={city_guid ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
          </GridLayout>
          <StateChangeWatcher
            StateList={StateList}
            formRenderProps={formRenderProps}
          />
        </FormElement>
      )}
    />
  );
};

export default CreateCity;
